// react
import { ReactNode, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Project, User } from '../../../../../@types';
import { flagCodes } from '../../../../../@types/unions';

// context
import { ContextProject } from '../../Project.context';

// hooks
import { useFetch } from '../../../../../hooks/useFetch';

// mui
import { Container, Grid, Typography } from '@mui/material';

// cards
import { CardDetails, CardDetailsProps } from '../../../../cards/CardDetails/CardDetails';
import { CardTraceability } from '../../../../cards/CardTraceability/CardTraceability';
import { CardUtilization } from '../../../../cards/CardUtilization/CardUtilization';

// ui
import { Flag } from '../../../../ui/Flag/Flag';
import { Loading } from '../../../../ui/Loading/Loading';

// config
import { featureFlags } from '../../../../../config/featureFlags';

// props
export interface DetailsProps {
	project: Project;
}

export const Details = ({ project }: DetailsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { isOnboarded, setProject } = useContext(ContextProject);

	// defaults
	let usersDefault: User[] = [];

	if (!isOnboarded && project.users.length) {
		usersDefault = project.users.filter((u) => u.role === 'TTL');
	}

	// state
	const [users, setUsers] = useState<User[]>(usersDefault);

	// fetch project utilization and traceability
	const { isLoading: isLoadingProject } = useFetch({
		isEnabled: Boolean(isOnboarded && project),
		url: `${process.env.REACT_APP_API_URL}/projects/${project.id}?fields=traceability,utilization`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]) {
				setProject((projectOld) => {
					if (projectOld) {
						return {
							...projectOld,
							traceability: res.data[0].traceability,
							utilization: res.data[0].utilization,
						};
					}

					return projectOld;
				});
			}
		},
	});

	// fetch project from WBG API to get users
	const { isLoading: isLoadingWbg } = useFetch({
		isEnabled: Boolean(isOnboarded && project),
		url: `${process.env.REACT_APP_API_URL}/wbg?q=${project.idWbg}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]?.users) {
				setUsers(res.data?.[0]?.users);
			}
		},
	});

	const data: CardDetailsProps['data'] = [
		{
			GridProps: { xs: 12, md: 12 },
			label: t('country'),
			value: (
				<Grid container alignItems="center" spacing={1}>
					{flagCodes.includes(project.country.code) && (
						<Grid item>
							<Flag code={project.country.code}></Flag>
						</Grid>
					)}
					<Grid flex={1} item>
						<Typography>{project.country.name}</Typography>
					</Grid>
				</Grid>
			),
		},
		{
			GridProps: { xs: 12, md: 12 },
			label: t('region'),
			value: project.region?.name,
		},
	];

	// add currencies information
	if (project.currencies && project.currencies.length > 0) {
		data.push({
			GridProps: { xs: 12, md: 12 },
			label: t('currencies'),
			value: [...new Set(project.currencies.map((c) => c.currency))].join(', '),
		});
	}

	let valueTtl: ReactNode = t('na');

	if (isLoadingWbg) {
		valueTtl = <Loading size="xs" />;
	} else if (users.length) {
		valueTtl = `${users[0].firstName} ${users[0].lastName}`;
	}

	data.push({
		GridProps: { xs: 12, md: 12 },
		label: t('taskTeamLeader'),
		value: valueTtl,
	});

	const colWidthDetails = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	const colWidthUtilization = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	const colWidthTraceability = {
		xs: 12,
		md: 12,
		lg: 4,
	};

	if (featureFlags['FINANCIAL_TRACEABILITY']) {
		colWidthDetails.lg = 3;
		colWidthUtilization.lg = 3;
		colWidthTraceability.md = 6;
		colWidthTraceability.lg = 3;
	}

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h2">{t('overview')}</Typography>
				</Grid>
				<Grid item {...colWidthDetails}>
					<CardDetails data={data} />
				</Grid>
				<Grid item {...colWidthUtilization}>
					<CardUtilization
						DialogProps={{
							subtitle: {
								children: project.name,
							},
							type: 'PROJECT',
							utilization: project.utilization,
						}}
						isLoading={isLoadingProject}
					/>
				</Grid>

				<Grid item {...colWidthTraceability}>
					<CardTraceability
						DialogProps={{
							subtitle: {
								children: project.name,
							},
						}}
						isLoading={isLoadingProject}
						traceability={project.traceability}
						type="TRANSACTIONS"
					/>
				</Grid>
				{featureFlags['FINANCIAL_TRACEABILITY'] && (
					<Grid item {...colWidthTraceability}>
						<CardTraceability
							DialogProps={{
								subtitle: {
									children: project.name,
								},
							}}
							isLoading={isLoadingProject}
							traceability={project.traceability}
							type="FINANCIAL"
						/>
					</Grid>
				)}
			</Grid>
		</Container>
	);
};
